import {
  put,
  call,
  take,
  race,
  delay,
  cancel,
  select,
  takeLatest,
  takeEvery
} from 'redux-saga/effects';
import {
  RUN_GET_ACTIVE_ORDERS_JOB,
  SET_ACTIVE_ORDERS,
  SET_NEW_ORDERS,
  CHANGE_STATUS_ACTIVE,
  CHANGE_STATUS_ACTIVE_SUCCESS,
  STOP_GET_ACTIVE_ORDERS_JOB
} from 'store/global/ordersV2/action';
import {getActiveOrdersApi} from 'helpers/services/api/ordersV2';
import {
  ORDER,
  ORDER_CANCELLATION_STATUS,
  ORDER_SOURCE,
  ORDER_STATUS
} from 'helpers/constants/orders_v2';
import {changeStatus} from 'store/ordersV2/orderList/saga';
import {getStorageToken} from 'helpers/localStorage/authStorage';
import {errorLog, infoLog} from 'services/logger/new-logger';
import {OPEN_CANCELLATION_REFUND_MODAL} from 'store/global/ordersV2/CancellationRefund/action';
import {
  OPEN_CANCELLATION_CONFIRMATION_MODAL_V2,
  SET_LOADING_CANCELLATION_V2
} from 'store/global/modals/action';

const GET_NEW_ORDERS_INTERVAL = 30000;

const currentActiveOrders = ({activeOrdersV2Reducer}) =>
  activeOrdersV2Reducer.activeOrders;

const userState = ({user}) => user;

export function* getActiveOrdersJob(stopNotificationSound) {
  while (true) {
    try {
      if (stopNotificationSound) {
        stopNotificationSound();
      }

      const {Timezone, _id: locationId} = yield select(
        (state) => state.user.activeLocation
      );

      const {data} = yield getActiveOrdersApi(locationId);

      const currentOrders = yield select(currentActiveOrders);

      yield put(SET_ACTIVE_ORDERS({data: data || [], Timezone}));

      infoLog(
        'ORDERS_POLLING_FRONTEND_SUCCESS',
        {
          Orders: data?.map((order) => order?.id)
        },
        yield select(userState)
      );

      const diff = currentOrders
        ? data?.filter(
            (newOrder) =>
              !currentOrders?.filter((order) => order.id === newOrder.id)[0] &&
              (!newOrder[ORDER.ORDER_STATUS] ||
                newOrder[ORDER.ORDER_STATUS] === ORDER_STATUS.NEW)
          )
        : [];

      if (diff.length > 0) {
        yield put(SET_NEW_ORDERS(diff));
      }
    } catch (error) {
      errorLog(
        'ORDERS_POLLING_FRONTEND_ERROR',
        {},
        error,
        yield select(userState)
      );
    }

    yield delay(GET_NEW_ORDERS_INTERVAL);
  }
}

function* runGetActiveOrdersJob({payload}) {
  try {
    const isOnline = yield select(
      (state) => state.globalNotificationsReducer?.isNetworkOnline
    );
    const userData = yield select((state) => state.user.userData);
    const locationId = yield select((state) => state.user.activeLocation?._id);
    const {token} = getStorageToken();

    if (
      !!window.AndroidDashboard ||
      !isOnline ||
      !token ||
      !userData.role ||
      !locationId ||
      userData.role === 'sudo'
    ) {
      return;
    }

    const {stop} = yield race({
      job: call(() => getActiveOrdersJob(payload?.onPollAction)),
      stop: take(STOP_GET_ACTIVE_ORDERS_JOB)
    });

    if (stop) {
      yield cancel();
    }
  } catch (error) {
    errorLog(
      'ORDERS_POLLING_FRONTEND_ERROR',
      {},
      error,
      yield select(userState)
    );
    console.log('SAGA ERROR', error);
  }
}

export function* changeStatusActiveOrder({
  payload: {id, status, previousStatus},
  orderId = null
}) {
  try {
    yield put(SET_LOADING_CANCELLATION_V2(true));
    const data = yield changeStatus({payload: {id, status, previousStatus}});

    if (data && data?.order[ORDER.ID]) {
      if (data?.order[ORDER.ORDER_STATUS] === ORDER_STATUS.COMPLETED) {
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({[ORDER.ID]: data?.order[ORDER.ID]})
        );
      }
      if (data?.order[ORDER.ORDER_STATUS] === ORDER_STATUS.CANCELLED) {
        yield put(
          OPEN_CANCELLATION_CONFIRMATION_MODAL_V2({
            isOpen: false,
            info: null,
            source: null,
            deliveryId: null
          })
        );
        yield put(
          OPEN_CANCELLATION_REFUND_MODAL({
            isOpenModal: true,
            invoiceId:
              (!data?.order[ORDER.REFUND] ||
                !data?.order[ORDER.REFUND]?.find((el) => el.type === 'Full')) &&
              data?.order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
                ? data?.order[ORDER.INVOICE_ID]
                : null,
            showCannotDeliveryText:
              (data?.orderProviderResult?.status &&
                data?.orderProviderResult?.status !==
                  ORDER_CANCELLATION_STATUS.CANCELLED_SUCCESSFULLY) ||
              data?.orderProviderResult?.errorMessage,
            orderId
          })
        );
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({[ORDER.ID]: data?.order[ORDER.ID]})
        );
      } else {
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({
            [ORDER.ORDER_STATUS]: data?.order[ORDER.ORDER_STATUS],
            [ORDER.ID]: data?.order[ORDER.ID]
          })
        );
      }
    }
    yield put(SET_LOADING_CANCELLATION_V2(false));
    return data?.order[ORDER.ORDER_STATUS];
  } catch (error) {
    yield put(SET_LOADING_CANCELLATION_V2(false));
    console.log('SAGA ERROR', error);
    return false;
  }
}

function* activeOrdersV2Saga() {
  yield takeLatest(RUN_GET_ACTIVE_ORDERS_JOB, runGetActiveOrdersJob);
  yield takeEvery(CHANGE_STATUS_ACTIVE, changeStatusActiveOrder);
}

export default activeOrdersV2Saga;
